import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { InstallStep } from '../../../models/InstallStep.interface';
import { environment } from '../../../../environments/environment';

/**
 * app-pwa-instructions
 *
 * The install instructions themselves — intro, numbered steps (heading, body and
 * screenshot each) and closing note — and nothing else. Content only: no header,
 * no toolbar, no close button, so the same markup can sit in a full page or in a
 * modal without either surface fighting the other for the chrome.
 *
 * This is the ONE copy of the instructions. Both surfaces render it:
 *   - {@link InstallHelpComponent} — the public `/install-help` route, for
 *     someone installing the app before they have a session.
 *   - {@link PwaInstructionsModalComponent} — the same content inside the
 *     authenticated app, opened from a help/info button.
 * Anything else that needs to explain installing should embed this too rather
 * than restate the steps, so the wording can only be wrong in one place.
 *
 * Source: notes/Web App/Documentation/PWA_Download_Instructions.docx. The
 * screenshots are that document's images, exported to assets/install-help/.
 * They were captured on iPhone/Safari; the step text names the Android
 * equivalent where it differs, which is why the images are captioned as iPhone
 * rather than presented as what every device looks like.
 *
 * Standalone + self-styled: the app's global stylesheet carries brand tokens and
 * modal shells but no step-list layout, and the mobile flow's card styles live
 * under the global `.receiving-mobile` scope, which does not reach a public page.
 * Colors and spacing come from the theme variables; only the layout is local.
 */
@Component({
  selector: 'app-pwa-instructions',
  standalone: true,
  imports: [CommonModule, IonicModule],
  templateUrl: './pwa-instructions.component.html',
  styleUrls: ['./pwa-instructions.component.css'],
})
export class PwaInstructionsComponent {
  /**
   * Heading rendered above the intro. The page shows it; the modal passes an
   * empty string because its toolbar title already says the same thing.
   */
  @Input() heading = 'How to Download ReceivingMgr as a PWA';

  /** The app address the user types into their mobile browser (step 1). */
  public readonly appUrl = environment.appUrl;

  /** Lead-in above the steps, explaining what installing gets them. */
  public readonly intro =
    'ReceivingMgr can be installed on your device as a Progressive Web App ' +
    '(PWA), giving you a native app-like experience without needing to visit ' +
    'the website each time.';

  /** Closing reassurance below the last step. */
  public readonly closing =
    'Done! You’re all set. The app will work on your device with the same ' +
    'functionality as the browser version, and you’ll have quick access ' +
    'from your home screen.';

  /**
   * The five install steps, in order, as written in the source document. The
   * step number shown to the user is the array index + 1 rather than a stored
   * field, so inserting a step cannot leave the numbering stale.
   */
  public readonly steps: InstallStep[] = [
    {
      title: 'Open ReceivingMgr in Your Browser',
      // The URL is interpolated by the template so it tracks the environment
      // rather than being hardcoded in the copy.
      body:
        'Navigate to {{appUrl}} in your mobile browser (Safari on iPhone/iPad, ' +
        'Chrome on Android). No need to log in here — you will have to log ' +
        'in again on the installed app.',
      image: 'assets/install-help/step-1-open-in-browser.png',
      alt: 'ReceivingMgr open in mobile Safari, with the browser menu button highlighted in the bottom bar.',
    },
    {
      title: 'Access the Share Menu',
      body:
        'Tap the Share button. On iPhone, this is the square with an arrow. On ' +
        'Android, it’s typically the three-dot menu followed by “Share”.',
      image: 'assets/install-help/step-2-share-menu.png',
      alt: 'The Safari browser menu open, with the Share option highlighted.',
    },
    {
      title: 'Select "Add to Home Screen"',
      body:
        'Look for the option “Add to Home Screen” (iPhone/iPad) or ' +
        '“Install app” (Android). Tap this option.',
      image: 'assets/install-help/step-3-add-to-home-screen.png',
      alt: 'The iOS share sheet scrolled down, with "Add to Home Screen" highlighted at the bottom of the list.',
    },
    {
      title: 'Confirm Installation',
      body:
        'Review the app name (should show as “ReceivingMgr”) and tap ' +
        '“Add” to confirm.',
      image: 'assets/install-help/step-4-confirm-install.png',
      alt: 'The iOS "Add to Home Screen" dialog showing the app name field and the Add button.',
    },
    {
      title: 'Launch Your New App',
      body:
        'ReceivingMgr will now appear on your home screen as an icon. Tap it ' +
        'anytime to open the app directly — no browser needed.',
      image: 'assets/install-help/step-5-launch-from-home-screen.png',
      alt: 'An iPhone home screen with the installed ReceivingMgr icon highlighted among the other apps.',
    },
  ];

  /**
   * Fills the `{{appUrl}}` placeholder in a step's body with this environment's
   * app address, so the printed instructions point at the build the user is
   * actually looking at (UAT vs production) instead of a hardcoded link.
   *
   * @param body The step body as authored, possibly containing `{{appUrl}}`.
   * @returns The body with the placeholder replaced.
   */
  public resolveBody(body: string): string {
    return body.replace('{{appUrl}}', this.appUrl);
  }
}
